import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { Tooltip } from '../../_atoms'
import CoachChatAttachmentModal from './CoachChatAttachmentModal'
import { useCoachChat } from './CoachChatContext'

// CSS styles for auto-growing textarea
const growWrapStyles = `
  .grow-wrap {
    display: grid;
    align-self: end;
  }
  
  .grow-wrap::after {
    content: attr(data-replicated-value) " ";
    white-space: pre-wrap;
    visibility: hidden;
    border-radius: 20px;
    padding: 12px 16px;
    font: inherit;
    font-size: 16px;
    line-height: 1.4;
    min-height: 48px;
    max-height: 200px;
    box-sizing: border-box;
    grid-area: 1 / 1 / 2 / 2;
    border: 1px solid transparent;
  }
  
  .grow-wrap > textarea {
    resize: none;
    overflow: hidden;
    border-radius: 20px;
    padding: 12px 16px;
    font: inherit;
    font-size: 16px;
    line-height: 1.4;
    min-height: 48px;
    max-height: 200px;
    box-sizing: border-box;
    grid-area: 1 / 1 / 2 / 2;
    transition: height 0.1s ease-out;
    border: none;
  }
`

/**
 * CoachTextMessageEditor component - provides text input and send functionality
 * Based on the candidate text message editor but adapted for coach use
 *
 * @param {Object} props
 * @param {Object} props.candidate - The candidate being chatted with
 * @param {Object} props.selectedCandidate - The selected candidate for this chat slot
 */
const CoachTextMessageEditor = ({ candidate, selectedCandidate }) => {
  const { chatManager, connectionState, getInputClass } = useCoachChat()
  const { t } = useTranslation('coach_chat')
  const [message, setMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const textareaRef = useRef(null)
  const [candidateId, setCandidateId] = useState(null)

  // Get candidate ID from candidate info (use selectedCandidate if available)
  useEffect(() => {
    const effectiveCandidate = selectedCandidate || candidate
    if (!effectiveCandidate || !chatManager) {
      setCandidateId(null)
      return
    }

    // Get candidate id directly from chat slot data (most reliable)
    const chatKey = effectiveCandidate.chatKey
    const slot =
      chatKey && connectionState.chats ? connectionState.chats[chatKey] : null

    // Check if candidate numeric id is already stored in slot data
    if (slot?.candidateNumericId) {
      setCandidateId(slot.candidateNumericId)
      // Still set up event listener in case it updates later
    }

    // Try to get candidate token for cache lookup and event listener
    const historyCandidateToken = slot?.chatHistory?.candidate_token
    const easyrtc = chatManager?.rtcService?.getEasyRTC()
    const apiFieldCandidateToken =
      (easyrtc &&
        effectiveCandidate?.easyrtcid &&
        easyrtc.getRoomApiField(
          connectionState.chatRoom,
          effectiveCandidate.easyrtcid,
          'chatToken',
        )) ||
      effectiveCandidate?.username
    const candidateToken = historyCandidateToken || apiFieldCandidateToken

    // Check if candidate info is already available
    // First try exact match with candidateToken
    let candidateInfo = null
    if (candidateToken && chatManager.candidateInfo?.[candidateToken]) {
      candidateInfo = chatManager.candidateInfo[candidateToken]
    } else if (chatManager.candidateInfo) {
      // Fallback: iterate through candidateInfo cache to find any entry with an id
      // (since there's typically only one active candidate at a time)
      const candidateInfoKeys = Object.keys(chatManager.candidateInfo)
      for (const key of candidateInfoKeys) {
        const info = chatManager.candidateInfo[key]
        if (info && info.id) {
          candidateInfo = info
          break
        }
      }
    }

    if (candidateInfo) {
      const resolvedId = candidateInfo.id
      if (resolvedId) {
        setCandidateId(resolvedId)
      }
    }

    // Listen for candidate info to be fetched (updates slot data and candidateId)
    const handleCandidateInfoFetched = (data) => {
      if (data.chatToken !== candidateToken || !data.candidate) {
        return
      }

      const candidateInfo = data.candidate
      const resolvedId = candidateInfo.id

      // Update slot data with candidate id (in case it wasn't set yet)
      if (resolvedId && slot) {
        slot.candidateNumericId = resolvedId
        slot.candidateId = resolvedId
      }

      if (resolvedId) {
        setCandidateId(resolvedId)
      }
    }

    if (candidateToken) {
      chatManager.addEventListener(
        'candidateInfoFetched',
        handleCandidateInfoFetched,
      )
    }

    return () => {
      if (candidateToken) {
        chatManager.removeEventListener(
          'candidateInfoFetched',
          handleCandidateInfoFetched,
        )
      }
    }
  }, [candidate, selectedCandidate, chatManager, connectionState.chatRoom])

  // Auto-resize textarea as content grows using CSS Grid approach
  useEffect(() => {
    if (textareaRef.current) {
      const growWrap = textareaRef.current.parentNode
      if (growWrap) {
        growWrap.dataset.replicatedValue = message
      }
    }
  }, [message])

  // Handle message input change
  const handleMessageChange = (event) => {
    const value = event.target.value
    setMessage(value)

    // Send typing indicator when coach starts typing
    if (value && !isTyping && chatManager && selectedCandidate) {
      setIsTyping(true)

      try {
        chatManager.sendTypingIndicator(null, selectedCandidate)
      } catch (error) {
        console.error('Error sending typing indicator:', error)
        setIsTyping(false)
        return
      }

      // Clear typing indicator after a delay
      setTimeout(() => setIsTyping(false), 3000)
    }
  }

  // Memoize the attachment upload handler
  const handleAttachmentUploaded = useCallback(
    (attachmentData) => {
      // Handle attachment upload success - send coach_action message
      const effectiveCandidate = selectedCandidate || candidate
      if (
        chatManager &&
        attachmentData?.attachment?.shareable_url &&
        effectiveCandidate
      ) {
        // Find the active chat for this candidate to get chatId and chatSlot
        const activeChat = connectionState.activeChats?.find(
          (chat) =>
            chat.candidateEasyrtcid === effectiveCandidate.easyrtcid ||
            chat.candidateId === effectiveCandidate.easyrtcid,
        )

        const action = {
          action_type: 'chat_attachment',
          action_target: attachmentData.attachment.shareable_url,
          attachment_name: attachmentData.attachment.name,
          attachment_id: attachmentData.attachment.id,
          attachment_size: attachmentData.attachment.file_size,
          attachment_type: attachmentData.attachment.content_type,
        }

        // Format action text for attachment
        const fileName = attachmentData.attachment.name || 'file'
        const actionText = `sent ${fileName}`

        // Create a message object to display the attachment on coach side
        const attachmentMessage = {
          id: Date.now(),
          type: 'coach_action',
          actionType: 'chat_attachment',
          actionData: action,
          text: actionText, // Add text field for chat history
          timestamp: new Date().toISOString(),
          sender: 'coach',
          senderName: 'me',
          chatId: activeChat?.id,
          chatSlot: activeChat?.chatSlot,
          chatKey: activeChat?.chatKey,
          attachment: {
            name: attachmentData.attachment.name,
            size: attachmentData.attachment.file_size,
            type: attachmentData.attachment.content_type,
            url: attachmentData.attachment.shareable_url,
            id: attachmentData.attachment.id,
          },
        }

        // Add the attachment message to the messages array
        chatManager.addMessage(attachmentMessage)

        // Log coach_action event
        if (chatManager.logEvent && activeChat?.conversationId) {
          chatManager.logEvent(
            'coach_action',
            {
              who: 'coach',
              easyrtcId: connectionState.myEasyrtcid,
              action: action.action_type,
              action_target: action.action_target,
            },
            activeChat.conversationId,
          )
        }

        // Send the coach_action to the candidate via EasyRTC
        const easyrtc = chatManager.rtcService?.getEasyRTC()
        if (easyrtc && effectiveCandidate?.easyrtcid) {
          easyrtc.sendData(effectiveCandidate.easyrtcid, 'coach_action', action)
        }
      }
    },
    [
      chatManager,
      candidate,
      selectedCandidate,
      connectionState.activeChats,
      connectionState.myEasyrtcid,
    ],
  )

  // Handle sending message
  const handleSendMessage = () => {
    const trimmedMessage = message.trim()
    if (!trimmedMessage || !selectedCandidate) return

    // Send the message via chat manager
    if (chatManager) {
      try {
        chatManager.sendMessage(trimmedMessage, null, selectedCandidate)
      } catch (error) {
        console.error('Error sending message:', error)
        return
      }
    }

    // Clear input and reset typing state
    setMessage('')
    setIsTyping(false)

    // Reset grow-wrap data attribute for auto-resize
    if (textareaRef.current) {
      const growWrap = textareaRef.current.parentNode
      if (growWrap) {
        growWrap.dataset.replicatedValue = ''
      }
    }
  }

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const isSendDisabled = !message.trim() || !selectedCandidate

  return (
    <div className="coach-text-message-editor px-6 py-4">
      {/* Inject CSS styles for grow-wrap */}
      <style>{growWrapStyles}</style>

      {/* Message input area - full width */}
      <div className="coach-text-message-editor-input-area mb-3">
        <div className="grow-wrap" data-replicated-value={message}>
          <textarea
            id="coach-text-message-compose-input"
            className={`w-full border font-['Roboto'] text-base font-normal focus:outline-none focus:ring-2 focus:ring-opacity-20 ${getInputClass()}`}
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyPress}
            placeholder="Type Message..."
            disabled={!selectedCandidate}
          />
        </div>
      </div>

      {/* Bottom controls row - attachment and send buttons */}
      <div className="flex items-center justify-end">
        {/* Right side controls - attachment and send buttons */}
        <div className="flex items-center gap-2">
          {/* Attachment button */}
          <CoachChatAttachmentModal
            candidateId={candidateId}
            onAttachmentUploaded={handleAttachmentUploaded}
          />

          {/* Send button — CTS-9777 / CC-UX-011 */}
          <Tooltip
            content={t('message_editor.send_message', {
              defaultValue: 'Send message',
            })}
            placement="top"
          >
            <button
              type="button"
              className="h-10 rounded-[20px] bg-green-800 px-6 font-['Roboto'] text-base font-medium text-slate-50 hover:bg-green-900 disabled:cursor-not-allowed disabled:bg-gray-400"
              onClick={handleSendMessage}
              disabled={isSendDisabled}
              title={t('message_editor.send_message', {
                defaultValue: 'Send message',
              })}
              aria-label={t('message_editor.send_message', {
                defaultValue: 'Send message',
              })}
            >
              {t('common.send', { defaultValue: 'Send' })}
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

export default CoachTextMessageEditor
